import { useCreateRoute } from 'components/create-dialog/CreateDialogContext';
import { draftIdIn } from 'components/create-dialog/useCreateDraft';
import Loader from 'components/Loader';
import { useMemo, useState } from 'react';
import { useGetEmployeeByIdQuery } from 'store/api/employees';
import { useGetSystemRolesListQuery } from 'store/api/systemRoles';
import { CustomField } from 'types/customFields';
import { useClassifiers } from 'utils/useClassifiers';
import { EMPLOYEE_FORM_CLASSIFIERS, employeeDtoToForm } from './employeeFormMapping';
import { EmployeeForm } from 'types/employees';

import { useCustomFields } from 'components/forms/inputs/custom-fields/useCustomFields';
import { EntityType } from 'types/entity';
import { formatDateStringInput } from 'utils/formatDateStringInput';
import EmployeePageForm from './EmployeePageForm';

/** The add form loaded with what a draft holds, read as the employee's edit page reads it. */
const EmployeeDraftAdd = ({ draftId }: { draftId: number }) => {
    const [draft, setDraft] = useState<{ initialData: EmployeeForm; customFields: CustomField[] }>();
    const { data: systemRoleList } = useGetSystemRolesListQuery();
    const { classifiers, isLoading: isLoadingClassifiers } = useClassifiers({ codes: EMPLOYEE_FORM_CLASSIFIERS });
    const { data: employee } = useGetEmployeeByIdQuery(String(draftId), {
        refetchOnMountOrArgChange: true,
    });
    const {
        customFields,
        initialCustomFieldsObject,
        isLoading: isLoadingCustomFieldData,
    } = useCustomFields({ entityType: EntityType.EMPLOYEE, fields: employee?.customFields });

    // Frozen once loaded: a refetch must not reset what the user is typing.
    if (!draft && employee && !isLoadingClassifiers && !isLoadingCustomFieldData) {
        setDraft({
            initialData: employeeDtoToForm(employee, { classifiers, customFieldValues: initialCustomFieldsObject }),
            customFields,
        });
    }

    return draft && systemRoleList ? (
        <EmployeePageForm
            type="add"
            initialData={draft.initialData}
            systemRoles={systemRoleList}
            entityCustomFields={draft.customFields}
        />
    ) : (
        <Loader />
    );
};

const EmployeeBlankAdd = () => {
    const { data: systemRoleList, isLoading: isLoadingSystemRoles } = useGetSystemRolesListQuery();

    const { initialCustomFieldsObject, isLoading: isLoadingCustomFieldData } = useCustomFields({
        entityType: EntityType.EMPLOYEE,
    });

    // Set initial form data (derived from system roles + custom-field defaults; render-time compute).
    const initialData = useMemo<EmployeeForm | undefined>(() => {
        if (isLoadingCustomFieldData || !systemRoleList) {
            return undefined;
        }
        const regularUser = systemRoleList.find((val) => val.code === 'TENANT_USER');
        return {
            cognitoId: null,
            firstName: '',
            lastName: '',
            localFirstName: '',
            localLastName: '',
            personTitle: null,
            isActive: true,
            personalIdCode: '',
            workEmailAddress: '',
            workPhone: '',
            mobilePhone: '',
            notes: '',
            previousNames: '',
            office: null,
            manager: null,
            employmentHistory: [],
            affiliations: [],
            systemRoles: regularUser ? [{ id: regularUser.id, name: regularUser.name }] : [],
            coreTeam: null,
            supportTeams: [],
            chairmanTeams: [],
            practiceAreas: [],
            languagesSpoken: [],
            taskReceivers: [],
            receivingTasksForEmployees: [],
            canImpersonate: [],
            employmentStartDate: formatDateStringInput(new Date()),
            employmentLeaveDate: null,
            birthdayDate: null,
            sensitiveInfo: '',
            isPoliticallyExposedPerson: false,
            personalAddress: null,
            profilePictureName: null,
            signaturePictureName: null,
            vatRegNum: '',
            genderType: null,
            partnershipPeerGroup: null,
            overwriteSignedByEmployeeId: null,
            ...initialCustomFieldsObject,
        };
    }, [systemRoleList, initialCustomFieldsObject, isLoadingCustomFieldData]);

    if (isLoadingSystemRoles || initialData === undefined || isLoadingCustomFieldData) {
        return <Loader />;
    }

    return <EmployeePageForm type="add" initialData={initialData} systemRoles={systemRoleList} />;
};

/** The employee create; its URL with `?draft=<id>` edits that draft instead of a blank employee. */
const EmployeePageAdd = () => {
    const draftId = draftIdIn(useCreateRoute().location.search);
    return draftId === null ? <EmployeeBlankAdd /> : <EmployeeDraftAdd key={draftId} draftId={draftId} />;
};

export default EmployeePageAdd;
